<template>
  <div class="biometric-container">
    <div v-if="loading && !isPolling" class="loading-wrapper">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      <p class="mt-3 text-gray-600">Подготовка к биометрической верификации...</p>
    </div>

    <!-- Режим активного опроса статуса верификации -->
    <div v-else-if="isPolling" class="polling-wrapper bg-blue-50 border border-blue-200 rounded-lg p-6 my-4 text-center">
      <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <h3 class="text-lg font-semibold text-blue-900 mb-2">Обработка верификации личности</h3>
      <p class="text-blue-800 font-medium mb-2">{{ pollingStatusText }}</p>
      
      <div class="bg-white rounded-md p-3 max-w-md mx-auto my-3 border border-blue-100 text-sm text-gray-600">
        <div class="flex items-center justify-center gap-2 mb-1 text-blue-700 font-medium">
          <span>📲 Проверьте ваш телефон</span>
        </div>
        <p>Если поступило SMS от <strong>1414</strong>, отправьте ответное согласие (например, код <strong>511</strong>) или подтвердите в приложении <strong>eGov Mobile</strong>.</p>
      </div>

      <div class="text-xs text-gray-500 mt-3">
        Проверка статуса: попытка {{ pollAttempt }} из {{ maxPollAttempts }}
      </div>

      <div class="mt-4 flex justify-center gap-3">
        <button 
          @click="checkVerificationResult(true)" 
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition"
        >
          Проверить сейчас
        </button>
        <button 
          @click="stopPollingAndReset" 
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium transition"
        >
          Отмена
        </button>
      </div>
    </div>
    
    <div v-else-if="error" class="error-wrapper">
      <div class="bg-red-50 p-6 rounded-md text-red-700 border border-red-200">
        <h3 class="font-semibold text-lg mb-2">{{ $t('eds.verificationFailed') }}</h3>
        <p class="mb-4">{{ $errorMessage(errorMessage) }}</p>
        
        <div class="bg-white p-3 rounded border border-red-100 text-sm text-gray-700 text-left mb-4">
          <p class="font-semibold mb-1 text-red-800">Что можно проверить:</p>
          <ul class="list-disc pl-5 space-y-1 text-gray-600">
            <li>Привязан ли ваш номер телефона к вашему ИИН в <strong>eGov.kz (Базе мобильных граждан)</strong>.</li>
            <li>Ответили ли вы на SMS-сообщение от номера <strong>1414</strong>.</li>
            <li>Качество освещения и четкость камеры при распознавании лица.</li>
          </ul>
        </div>

        <button @click="retry" class="mt-2 px-5 py-2.5 bg-primary text-white rounded-md hover:bg-blue-600 font-medium transition">
          Попробовать снова
        </button>
      </div>
    </div>
    
    <div v-else-if="!sessionStarted" class="start-wrapper">
      <h2 class="text-xl font-semibold mb-4 text-gray-800">Биометрическая верификация</h2>
      <p class="mb-4 text-gray-600">
        Для продолжения регистрации вам необходимо пройти биометрическую верификацию личности:
      </p>

      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left w-full text-sm text-blue-900">
        <h4 class="font-semibold mb-2 flex items-center gap-1.5">
          <span>💡</span> Рекомендации для быстрого прохождения:
        </h4>
        <ul class="list-disc pl-5 space-y-1.5 text-blue-800">
          <li><strong>Держите телефон рядом:</strong> вам поступит SMS от <strong>1414</strong> для подтверждения согласия.</li>
          <li><strong>База мобильных граждан:</strong> номер телефона должен быть зарегистрирован в вашем профиле eGov.</li>
          <li><strong>Камера:</strong> обеспечьте хорошее освещение лица без темных очков и бликов.</li>
        </ul>
      </div>

      <button 
        @click="startBiometricVerification" 
        class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600 font-medium transition shadow-sm"
      >
        Начать верификацию
      </button>
    </div>
    
    <div v-else-if="verificationComplete" class="results-wrapper">
      <h2 class="text-xl font-semibold mb-4">Верификация завершена</h2>
      <div v-if="verificationSuccessful" class="success-wrapper">
        <div class="bg-green-50 p-4 rounded-md mb-6 border border-green-200">
          <p class="text-green-700 font-medium">✅ Биометрическая верификация успешно пройдена!</p>
        </div>
        
        <button @click="continueRegistration" class="mt-4 px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600 font-medium transition">
          Продолжить регистрацию
        </button>
      </div>
      <div v-else class="failure-wrapper">
        <div class="bg-red-50 p-4 rounded-md mb-6 border border-red-200">
          <p class="text-red-700 font-medium">{{ $t('errors.biometricFailed') }}</p>
        </div>
        <p class="mb-4 text-gray-600">Возможные причины:</p>
        <ul class="list-disc pl-5 mb-6 text-gray-600 text-left">
          <li>Не подтверждено согласие по SMS от 1414 или истекло время ожидания</li>
          <li>Номер телефона не зарегистрирован в Базе мобильных граждан eGov</li>
          <li>Низкое качество изображения или освещения при съемке лица</li>
          <li>Проверка была прервана пользователем</li>
        </ul>
        
        <button @click="retry" class="px-6 py-3 bg-primary text-white rounded-md hover:bg-blue-600 font-medium transition">
          Попробовать снова
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BiometricVerification',
  props: {
    userInfo: {
      type: Object,
      default: () => ({
        iin: '',
        phone: '',
        email: ''
      })
    }
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: '',
      sessionId: null,
      redirectUrl: null,
      sessionStarted: false,
      verificationComplete: false,
      verificationSuccessful: false,
      verificationResults: null,
      // Polling state
      isPolling: false,
      pollAttempt: 0,
      maxPollAttempts: 30, // 30 attempts * 2.5s = ~75 seconds
      pollTimer: null,
      pollingStatusText: 'Ожидаем подтверждение SMS от 1414...'
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async startBiometricVerification() {
      this.loading = true;
      try {
        const response = await fetch('/api/biometric/request/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            iin: this.userInfo.iin || '',
            phone: this.userInfo.phone || ''
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.session_id && data.biometric_url) {
          this.sessionId = data.session_id;
          this.redirectUrl = data.biometric_url;
          
          localStorage.setItem('biometricSessionId', this.sessionId);
          localStorage.setItem('biometricFlowToken', data.flow_token || '');
          
          this.sessionStarted = true;
          this.loading = false;
          
          // Redirect to Biometric platform
          window.location.href = this.redirectUrl;
        } else {
          throw new Error('Некорректный ответ от сервиса биометрии');
        }
      } catch (error) {
        this.error = true;
        this.errorMessage = this.$apiError(error, this.$t('errors.biometricSession'));
        this.loading = false;
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.isPolling = false;
    },

    stopPollingAndReset() {
      this.stopPolling();
      this.retry();
    },

    async checkVerificationResult(isManual = false) {
      if (isManual) {
        this.loading = true;
      }

      try {
        const sessionId = localStorage.getItem('biometricSessionId') || this.sessionId;
        const flowToken = localStorage.getItem('biometricFlowToken');
        
        if (!sessionId) {
          throw new Error('ID биометрической сессии не найден');
        }
        
        const query = new URLSearchParams({ session_id: sessionId, flow_token: flowToken || '' });
        const response = await fetch(`/api/biometric/result/?${query.toString()}`, { credentials: 'include' });
        
        if (!response.ok) {
          const errorJson = await response.json().catch(() => ({}));
          throw new Error(errorJson.error || `Ошибка сервера: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        this.verificationResults = data;
        
        // 1. Успешное завершение
        if (data.success === true) {
          this.stopPolling();
          this.verificationSuccessful = true;
          this.verificationComplete = true;
          this.loading = false;
          
          if (data.access_token) localStorage.setItem('authToken', data.access_token);
          if (data.user) localStorage.setItem('userData', JSON.stringify(data.user));
          
          this.$emit('verification-completed', {
            success: true,
            data: data
          });
          
          setTimeout(() => this.continueRegistration(), 800);
          return;
        }

        // 2. Промежуточный статус (сессия ещё обрабатывается / ждет согласия 1414)
        const vdata = data.verification_data || {};
        const rawStatus = String(data.status || vdata.status || '').toUpperCase();
        const kdpStatus = String(data.kdp_status || vdata.kdp_status || '').toUpperCase();
        
        const isPending = data.is_pending === true || 
          ['PENDING', 'CONSENT_STARTING', 'PROGRESS', 'CREATED', 'VALID'].includes(rawStatus) ||
          ['PENDING', 'CONSENT_STARTING', 'VALID'].includes(kdpStatus);

        const isTerminalFailure = ['FAILED', 'TIMEOUT', 'INVALID'].includes(rawStatus) || 
          ['TIMEOUT', 'FAILED', 'INVALID', 'REQUEST_RATE_LIMIT_EXCEEDED_ERROR'].includes(kdpStatus);

        if (isPending && !isTerminalFailure) {
          this.isPolling = true;
          this.loading = false;
          
          if (kdpStatus === 'VALID' || rawStatus === 'VALID') {
            this.pollingStatusText = 'Согласие получено! Загружаем цифровые документы и данные...';
          } else if (rawStatus === 'CONSENT_STARTING' || kdpStatus === 'CONSENT_STARTING') {
            this.pollingStatusText = data.message || 'Связываемся со шлюзом согласия 1414...';
          } else {
            this.pollingStatusText = data.message || 'Ожидаем подтверждения согласия по SMS 1414 (проверьте телефон)...';
          }

          if (this.pollAttempt < this.maxPollAttempts) {
            this.pollAttempt += 1;
            this.pollTimer = setTimeout(() => {
              this.checkVerificationResult(false);
            }, 2500);
            return;
          } else {
            // Превышено количество попыток опроса
            this.stopPolling();
            this.error = true;
            this.errorMessage = this.$t('errors.biometricConsentTimeout');
            this.loading = false;
            return;
          }
        }

        // 3. Окончательный сбой / таймаут
        this.stopPolling();
        this.verificationSuccessful = false;
        this.error = true;
        this.errorMessage = data.message || this.$t('errors.biometricIncomplete');
        this.loading = false;
        
        this.$emit('verification-completed', {
          success: false,
          data: data
        });

      } catch (error) {
        if (this.isPolling && this.pollAttempt < this.maxPollAttempts) {
          // При временной сетевой ошибке во время опроса пробуем повторить
          this.pollAttempt += 1;
          this.pollTimer = setTimeout(() => {
            this.checkVerificationResult(false);
          }, 3000);
        } else {
          this.stopPolling();
          this.error = true;
          this.errorMessage = this.$apiError(error, this.$t('errors.biometricResults'));
          this.loading = false;
        }
      }
    },
    
    continueRegistration() {
      if (this.verificationSuccessful && this.verificationResults) {
        this.$emit('registration-completed', {
          success: true,
          data: this.verificationResults
        });
      } else {
        this.$emit('continue-registration', this.verificationResults);
      }
    },
    
    retry() {
      this.stopPolling();
      this.loading = false;
      this.error = false;
      this.errorMessage = '';
      this.sessionId = null;
      this.redirectUrl = null;
      this.sessionStarted = false;
      this.verificationComplete = false;
      this.verificationSuccessful = false;
      this.verificationResults = null;
      this.pollAttempt = 0;
      
      localStorage.removeItem('biometricSessionId');
      localStorage.removeItem('biometricFlowToken');
    },
    
    handleBiometricReturn() {
      this.pollAttempt = 0;
      this.isPolling = true;
      this.pollingStatusText = 'Проверяем статус верификации...';
      
      setTimeout(() => {
        this.checkVerificationResult(false);
      }, 500);
    }
  },
  mounted() {
    const sessionId = localStorage.getItem('biometricSessionId');
    const urlParams = new URLSearchParams(window.location.search);
    const hasReturnParams = urlParams.has('status') || urlParams.has('flow_result') || urlParams.has('flow_session_result');
    
    if (hasReturnParams && sessionId) {
      this.sessionStarted = true;
      this.handleBiometricReturn();
      return;
    }
    
    if (sessionId && window.location.pathname.includes('/biometric-register')) {
      this.sessionStarted = true;
      this.handleBiometricReturn();
    }
  },
  
  watch: {
    '$route': {
      handler(newRoute) {
        const sessionId = localStorage.getItem('biometricSessionId');
        const hasReturnParams = newRoute.query.status || newRoute.query.flow_result || newRoute.query.flow_session_result;
        
        if (sessionId && hasReturnParams && !this.isPolling && !this.verificationComplete) {
          this.sessionStarted = true;
          this.handleBiometricReturn();
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.biometric-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.loading-wrapper, .error-wrapper, .start-wrapper, .results-wrapper, .polling-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.loading-wrapper {
  padding: 40px 0;
}

.error-wrapper, .failure-wrapper {
  width: 100%;
}

pre {
  text-align: left;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}
</style> 
